// Global Settings page - independent of the per-reporting PreflightWizardPage,
// configures the fund-wide PreflightSettings object (GET/PUT
// /compartment-report/settings). Mirrors reporting-hub's own
// SettingsPage.tsx: fetch into a draft, 4-tab TabView, single "Save settings"
// button gated on a dirty-check, warn on browser-close (and best-effort on
// in-app tab-away) while dirty.
//
// immer (use-immer) is NOT an exaport dependency (checked package.json) -
// this uses plain useState with explicit copy-on-write spreads instead of
// reporting-hub's useImmer.
//
// PUT /compartment-report/settings has replace-not-patch semantics: the body
// must be the FULL PreflightSettings object. `draft` always starts from the
// freshly-fetched object (including fields this page never edits -
// subscriptions, interest_distributions, outstanding_bids, misc_bank_cost,
// issue_dates, version - which belong to the separate dialog off the
// Calculation tab) and every update below only overwrites the sub-field it
// owns, so those other fields are carried through unchanged on save.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import ColumnRulesTable from './ColumnRulesTable';
import OperationalChecksBuilder from './OperationalChecksBuilder';
import ReconciliationToleranceForm from './ReconciliationToleranceForm';
import PageHeader from '../../common/PageHeader';
import LoadingState from '../../common/LoadingState';
import errorMessage from '../../../helpers/errorMessage';

// Mirrors reporting-hub's normalizeForSave: condition inputs are always
// typed as text, but OperationalCondition.value is float|string on the
// backend - coerce numeric-looking strings to numbers before PUTing so
// e.g. "valuation > 0" doesn't get persisted (and then compared) as "0".
const normalizeForSave = (settings) => ({
  ...settings,
  loantape: {
    ...settings.loantape,
    operational_checks: settings.loantape.operational_checks.map((check) => ({
      ...check,
      conditions: check.conditions.map((cond) => {
        if (typeof cond.value === 'string' && cond.value.trim() !== '' && !isNaN(Number(cond.value))) {
          return { ...cond, value: Number(cond.value) };
        }
        return cond;
      }),
    })),
  },
});

const TABS = ['LoanTape Columns', 'CashFlows Columns', 'Operational Checks', 'Reconciliation'];

const SettingsPage = ({ onBack, setLoading = () => {} }) => {
  const [draft, setDraft] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const isDirty = !!draft && !!lastSaved && JSON.stringify(draft) !== JSON.stringify(lastSaved);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosWithAuth.get('/compartment-report/settings');
      setDraft(res.data);
      setLastSaved(res.data);
      setError(null);
    } catch (err) {
      console.error('[SettingsPage] fetch error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to fetch settings.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Browser-close/refresh warning while dirty - browsers show their own
  // fixed text regardless of e.returnValue's content.
  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const handleBack = () => {
    if (isDirty && !window.confirm('You have unsaved settings changes. Leave without saving?')) {
      return;
    }
    onBack();
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setLoading(true);
    try {
      const res = await axiosWithAuth.put('/compartment-report/settings', normalizeForSave(draft));
      setDraft(res.data);
      setLastSaved(res.data);
      setError(null);
    } catch (err) {
      console.error('[SettingsPage] save error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to save settings.'));
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const updateLoanTapeColumns = (columns) => {
    setDraft((d) => ({ ...d, loantape: { ...d.loantape, columns } }));
  };

  const updateCashFlowsColumns = (columns) => {
    setDraft((d) => ({ ...d, cashflows: { ...d.cashflows, columns } }));
  };

  const updateOperationalChecks = (operational_checks) => {
    setDraft((d) => ({ ...d, loantape: { ...d.loantape, operational_checks } }));
  };

  const updateTolerance = (cashflow_reconciliation_tolerance) => {
    setDraft((d) => ({ ...d, cashflow_reconciliation_tolerance }));
  };

  if (error && !draft) {
    return (
      <div className="content-container">
        <div className="ex-inline-error">{error}</div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="content-container">
        <LoadingState label="Loading settings" />
      </div>
    );
  }

  return (
    <div className="content-container">
      <PageHeader
        title="Compartment Report Settings"
        icon="pi-sliders-h"
        subtitle={lastSaved?.updated_at ? `Last saved ${new Date(lastSaved.updated_at).toLocaleString()}` : 'No changes saved yet'}
        actions={
          <>
            <Button label="Back" icon="pi pi-arrow-left" className="p-button-secondary p-button-outlined" onClick={handleBack} />
            <Button label={saving ? 'Saving...' : 'Save settings'} icon="pi pi-save" disabled={!isDirty || saving} onClick={handleSave} />
          </>
        }
      />

      {error && <div className="ex-inline-error mb-3">{error}</div>}

      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header={TABS[0]}>
          <p className="cr-help-text">
            Present / Complete / Data type rules checked against every LoanTape upload.
          </p>
          <ColumnRulesTable rules={draft.loantape.columns} onChange={updateLoanTapeColumns} />
        </TabPanel>

        <TabPanel header={TABS[1]}>
          <p className="cr-help-text">
            Same Present / Complete / Data type rules, applied to the CashFlows file instead.
          </p>
          <ColumnRulesTable rules={draft.cashflows.columns} onChange={updateCashFlowsColumns} />
        </TabPanel>

        <TabPanel header={TABS[2]}>
          <p className="cr-help-text">
            Free-form business rules on top of the Column Check, e.g. "valuation &gt;= 0".
          </p>
          <OperationalChecksBuilder checks={draft.loantape.operational_checks} onChange={updateOperationalChecks} />
        </TabPanel>

        <TabPanel header={TABS[3]}>
          <p className="cr-help-text">
            Tolerance for the "Cashflow Reconciliation Check": differences between the Exaloan Sum and the Originator
            Sum below this threshold count as OK instead of a mismatch.
          </p>
          <ReconciliationToleranceForm tolerance={draft.cashflow_reconciliation_tolerance} onChange={updateTolerance} />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default SettingsPage;
